/**
 * RCC Surface #17 — HealthNet Release.
 *
 * Wave 2 (locked 2026-04-27 "go all defaults" — decision #4: promote to
 * dedicated RCC surface). Coordinates the MO HealthNet (Medicaid) decedent
 * release-of-funds workflow per RSMo 473.398.6 + 473.444.
 *
 * Owner: Kent (HealthNet coordination with MO Department of Social Services).
 * Risk Mgmt does NOT review death paths (see memory
 * reference_deceased_customer_procedure_20260427).
 *
 * Wave 2 ships the surface + intake checklist + handoff event. Backend
 * service to commit the case + spawn the 13-month state-claim hold timer
 * is Wave 3 — this surface fires a structured SessionEvent in the
 * meantime so audit trail is preserved.
 */

import { useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

interface FormState {
  cifId: string;
  decedentName: string;
  dateOfDeath: string;
  releaseFormReceived: boolean;
  ssaLetter1: boolean;
  ssaLetter2: boolean;
  ssaLetter3: boolean;
  stateClaimSent: boolean;
  notes: string;
}

const INITIAL: FormState = {
  cifId: '',
  decedentName: '',
  dateOfDeath: new Date().toISOString().slice(0, 10),
  releaseFormReceived: false,
  ssaLetter1: false,
  ssaLetter2: false,
  ssaLetter3: false,
  stateClaimSent: false,
  notes: '',
};

export function HealthNetRelease(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [done, setDone] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const stateClaimDueAt = useMemo(() => {
    if (!form.dateOfDeath) return null;
    const d = new Date(form.dateOfDeath);
    if (Number.isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + 13);
    return d.toISOString().slice(0, 10);
  }, [form.dateOfDeath]);

  const submit = async (): Promise<void> => {
    if (!form.cifId.trim() || !form.decedentName.trim() || !form.releaseFormReceived) {
      setError('CIF ID, decedent name, and HealthNet Release form receipt are required.');
      return;
    }
    setError(null);
    try {
      await apiClient.retail.events.write({
        eventType: 'healthnet-release-recorded',
        eventCategory: 'compliance',
        severity: 'warn',
        actor: user?.upn ?? 'unknown',
        payloadJson: JSON.stringify({
          cifId: form.cifId.trim(),
          decedentName: form.decedentName.trim(),
          dateOfDeath: form.dateOfDeath,
          stateClaimDueAt,
          ssaLetters: { l1: form.ssaLetter1, l2: form.ssaLetter2, l3: form.ssaLetter3 },
          stateClaimSent: form.stateClaimSent,
          notes: form.notes.trim(),
        }),
      });
      setDone(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Submission failed.';
      setError(msg);
    }
  };

  if (done) {
    return (
      <div>
        <SurfaceHeader
          title="HealthNet Release"
          scope="MO HealthNet decedent release-of-funds coordination."
          surfaceNumber={17}
          role="FSR"
        />
        <div style={successStyle}>
          <strong>HealthNet release recorded.</strong> Kent has been notified via the
          <code> healthnet-release-recorded</code> SessionEvent.
          {stateClaimDueAt && <> 13-month state-claim hold ends <strong>{stateClaimDueAt}</strong>.</>}
        </div>
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Button variant="ghost" onClick={() => { setForm(INITIAL); setDone(false); }}>Record another</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SurfaceHeader
        title="HealthNet Release"
        scope="MO HealthNet (Medicaid) decedent release-of-funds. Owned by Kent; coordinates with MO DSS."
        surfaceNumber={17}
        role="FSR"
      />

      <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
        <div style={infoNoteStyle}>
          Per RSMo 473.398.6 + 473.444, MO HealthNet Division is treated as a "taxing authority". A 13-month
          state-claim hold runs from date of death. Funds cannot be disbursed until both (a) HealthNet Release
          form on file AND (b) state claim deadline passes (or claim received + reconciled).
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
          <Field label="CIF ID">
            <input type="text" value={form.cifId}
                   onChange={(e) => setForm({ ...form, cifId: e.target.value })}
                   style={inputStyle} />
          </Field>
          <Field label="Date of death">
            <input type="date" value={form.dateOfDeath}
                   onChange={(e) => setForm({ ...form, dateOfDeath: e.target.value })}
                   style={inputStyle} />
          </Field>
        </div>
        <Field label="Decedent name">
          <input type="text" value={form.decedentName}
                 onChange={(e) => setForm({ ...form, decedentName: e.target.value })}
                 style={inputStyle} />
        </Field>

        {stateClaimDueAt && (
          <div style={infoNoteStyle}>
            13-month state-claim hold ends: <strong>{stateClaimDueAt}</strong>
            {' '}<Pill variant="warn">soft-block</Pill>
          </div>
        )}

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Documentation received</legend>
          <CheckRow label="HealthNet Release form (REQUIRED)"
                    helper="Customer / Personal Rep submits to MO DSS; copy on file at PCB."
                    value={form.releaseFormReceived}
                    onChange={(v) => setForm({ ...form, releaseFormReceived: v })} />
          <CheckRow label="SSA Sample Letter 1 (Rep Payee)"
                    helper="Required when decedent was a Rep Payee."
                    value={form.ssaLetter1}
                    onChange={(v) => setForm({ ...form, ssaLetter1: v })} />
          <CheckRow label="SSA Sample Letter 2"
                    value={form.ssaLetter2}
                    onChange={(v) => setForm({ ...form, ssaLetter2: v })} />
          <CheckRow label="SSA Sample Letter 3"
                    value={form.ssaLetter3}
                    onChange={(v) => setForm({ ...form, ssaLetter3: v })} />
          <CheckRow label="State Claim sent / response received"
                    helper="Track whether MO DSS state claim was filed against the estate."
                    value={form.stateClaimSent}
                    onChange={(v) => setForm({ ...form, stateClaimSent: v })} />
        </fieldset>

        <Field label="Notes (Kent coordination, claim numbers, etc.)">
          <textarea value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>

        {error && <div style={errorStyle} role="alert">{error}</div>}

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <Button variant="primary" onClick={() => void submit()}>Record HealthNet release</Button>
        </div>
      </div>
    </div>
  );
}

function CheckRow({
  label, helper, value, onChange,
}: {
  label: string;
  helper?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-2) 0',
      cursor: 'pointer',
    }}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {helper && <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 2 }}>{helper}</div>}
      </div>
    </label>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--sp-3)',
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

const errorStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const infoNoteStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--info-bg)',
  color: 'var(--info-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const successStyle: React.CSSProperties = {
  padding: 'var(--sp-4)',
  background: 'var(--ok-bg, #e8f5e9)',
  color: 'var(--ok-text, #1b5e20)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-4)',
};

const legendStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--ink-500)',
  padding: '0 var(--sp-2)',
};
